import React, { useMemo, useState } from "react";
import { Marker } from "react-map-gl/mapbox";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { devWarn, logError } from "../../utils/logger";

// ============================================================================
// CONFIGURATION
// ============================================================================

// Solo avatar size (proportionate to pill cluster height)
const MARKER_SIZE = 36;

// Use CSS variable so markers follow theme changes automatically
const PRIMARY = "var(--clr-primary-default)";

// Stem + tip dimensions
const STEM_WIDTH = 2;
const STEM_HEIGHT = 12;
const TIP_SIZE = 4;

// ============================================================================
// INTERNAL COMPONENTS
// ============================================================================

/**
 * Initials - Fallback display when avatar image is unavailable
 */
export function Initials({ name, size }) {
  const initials = useMemo(() => {
    if (!name) return "?";
    return (
      name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase())
        .join("") || "?"
    );
  }, [name]);

  const fontSize = Math.max(10, Math.round(size / 3.2));

  return (
    <div className="flex h-full w-full items-center justify-center bg-surface-muted dark:bg-[#6b7280] text-text-secondary">
      <span className="font-semibold" style={{ fontSize }}>
        {initials}
      </span>
    </div>
  );
}

/**
 * AvatarFill - Handles image loading with fallback to initials
 */
export function AvatarFill({ src, name, size }) {
  const [broken, setBroken] = useState(false);

  return (
    <>
      {src && !broken ? (
        <img
          src={src}
          alt={name || "User avatar"}
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <Initials name={name} size={size} />
      )}
    </>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * AvatarMarker - Map pin marker with circular avatar and gradient stem
 */
// Entrance animation keyframes (injected once)
const MARKER_ENTRANCE_STYLE = `
@keyframes markerPopIn {
  from { transform: scale(0.5); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
`;
if (typeof document !== "undefined" && !document.getElementById("marker-pop-in")) {
  const style = document.createElement("style");
  style.id = "marker-pop-in";
  style.textContent = MARKER_ENTRANCE_STYLE;
  document.head.appendChild(style);
}

export default function AvatarMarker({ note, onClick, isSelected = false, zIndex }) {
  const { longitude, latitude, owner, visibility, isViewedByCurrentUser, isOwner } = note || {};
  const { avatar: avatarUrl, name } = owner || {};
  const isPrivate = visibility === "private";

  // Seen/unseen visual treatment (own notes treated as seen)
  const isViewed = isViewedByCurrentUser || isOwner;
  const isDark = document.documentElement.classList.contains("dark");
  const MUTED = isDark ? "#4b5563" : "#6b7280";
  const markerColor = isViewed ? MUTED : PRIMARY;
  const markerOpacity = isViewed ? 0.65 : 1;

  // Validate coordinates
  if (!longitude || !latitude || isNaN(longitude) || isNaN(latitude)) {
    devWarn("Invalid coordinates for avatar marker:", {
      longitude,
      latitude,
    });
    return null;
  }

  // -------------------------------------------------------------------------
  // Geometry
  // -------------------------------------------------------------------------

  const size = MARKER_SIZE;
  const lockIconSize = Math.round(size * 0.458);

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  const handleClick = (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(note);
    } catch (error) {
      logError("Error handling marker click:", error);
    }
  };

  const handleMouseEnter = (e) => {
    try {
      if (e.currentTarget) {
        e.currentTarget.style.transform = "translateY(-2px)";
      }
    } catch (error) {
      logError("Error handling mouse enter:", error);
    }
  };

  const handleMouseLeave = (e) => {
    try {
      if (e.currentTarget) {
        e.currentTarget.style.transform = "translateY(0px)";
      }
    } catch (error) {
      logError("Error handling mouse leave:", error);
    }
  };

  const handleMouseDown = (e) => {
    try {
      if (e.currentTarget) {
        e.currentTarget.style.transform = "translateY(0px)";
      }
    } catch (error) {
      logError("Error handling mouse down:", error);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="bottom"
      style={zIndex != null ? { zIndex } : undefined}
    >
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Open ${isPrivate ? "private " : ""}note by ${name || "unknown user"}`}
        title={name || "Note"}
        className="group relative focus:outline-none"
        style={{
          transform: "translateY(0px)",
          transition: "transform 120ms",
          animation: "markerPopIn 200ms ease-out",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
      >
        <div className="flex flex-col items-center" style={{ position: "relative", opacity: markerOpacity }}>
          {/* Avatar circle */}
          <div
            className="relative rounded-full overflow-hidden flex-shrink-0"
            style={{
              width: size,
              height: size,
              border: `2px solid ${markerColor}`,
              filter: isViewed ? "grayscale(100%)" : "none",
            }}
          >
            <AvatarFill src={avatarUrl} name={name} size={size} />

            {isPrivate && (
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.35)" }}
              >
                <LockClosedIcon
                  className="text-white drop-shadow-md"
                  style={{
                    width: lockIconSize,
                    height: lockIconSize,
                    opacity: 0.9,
                  }}
                />
              </div>
            )}
          </div>

          {/* Stem */}
          <div
            aria-hidden="true"
            style={{
              width: STEM_WIDTH,
              height: STEM_HEIGHT,
              background: `linear-gradient(to bottom, ${markerColor}, transparent)`,
            }}
          />

          {/* Tip dot */}
          <div
            aria-hidden="true"
            className="rounded-full"
            style={{
              width: TIP_SIZE,
              height: TIP_SIZE,
              backgroundColor: isViewed ? MUTED : "var(--clr-primary-400)",
              opacity: 0.5,
            }}
          />
        </div>
      </button>
    </Marker>
  );
}
